(() => {
  if (window.__peakstyleParagliderCartPreviewLoaded) return;
  window.__peakstyleParagliderCartPreviewLoaded = true;

  const svgCache = new Map();
  const productConfigCache = new Map();
  let refreshTimer = null;
  let refreshRunning = false;
  let refreshQueued = false;

  const rootUrl = () => (window.Shopify && Shopify.routes && Shopify.routes.root) || (window.FoxThemeSettings && FoxThemeSettings.routes && FoxThemeSettings.routes.root) || '/';
  const escapeId = (value) => (window.CSS && CSS.escape ? CSS.escape(String(value)) : String(value).replace(/[^a-zA-Z0-9_-]/g, '\\$&'));
  const parseData = (value, fallback = null) => {
    if (!value) return fallback;
    if (typeof value === 'object') return value;
    try { return JSON.parse(value); } catch (error) { return fallback; }
  };

  const injectStyles = () => {
    if (document.getElementById('PeakstyleParagliderCartPreviewStyles')) return;
    const style = document.createElement('style');
    style.id = 'PeakstyleParagliderCartPreviewStyles';
    style.textContent = '.peakstyle-paraglider-cart-preview{position:relative;display:flex;align-items:center;justify-content:center;width:100%;height:100%;min-height:88px;padding:5px;background:#f7f7f7;overflow:hidden}.peakstyle-paraglider-cart-preview svg{display:block;width:100%;height:auto;max-height:150px}.peakstyle-paraglider-cart-preview .cls-2{mix-blend-mode:multiply}.f-cart-item__media .peakstyle-paraglider-cart-preview{min-height:120px}.f-cart-drawer__product-image .peakstyle-paraglider-cart-preview{min-height:90px}';
    document.head.appendChild(style);
  };

  const getSvgText = (url) => {
    if (!svgCache.has(url)) {
      svgCache.set(url, fetch(url, { credentials: 'same-origin' }).then((response) => {
        if (!response.ok) throw new Error('Configured SVG could not be loaded');
        return response.text();
      }));
    }
    return svgCache.get(url);
  };

  const getLocalData = (properties) => {
    const id = properties && properties['_Konfigurations-ID'];
    if (!id) return null;
    try { return parseData(localStorage.getItem(`peakstyle-paraglider-config:${id}`)); }
    catch (error) { return null; }
  };

  const getProductConfig = async (item) => {
    const key = item.url || item.handle || String(item.product_id || '');
    if (!key) return null;
    if (!productConfigCache.has(key)) {
      productConfigCache.set(key, (async () => {
        const sourceUrl = item.url || `/products/${item.handle}`;
        const response = await fetch(sourceUrl, { credentials: 'same-origin', headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error('Product configuration page could not be loaded');
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const node = doc.querySelector('[data-paraglider-configurator-inline] [data-pg-config], [data-pg-config]');
        if (!node) return null;
        const raw = parseData(node.textContent, {});
        return {
          brand: raw.manufacturer || '',
          model: raw.model || '',
          svgUrl: raw.svgUrl || '',
          palette: parseData(raw.palette, {}),
          zoneMap: parseData(raw.zoneMap, {})
        };
      })().catch((error) => {
        console.warn('PeakStyle paraglider product configuration:', error);
        return null;
      }));
    }
    return productConfigCache.get(key);
  };

  const resolveData = async (item) => {
    const properties = item.properties || {};
    const compact = parseData(properties._Konfigurationsdaten, null);
    if (!compact) return null;
    const local = getLocalData(properties);
    if (local && local.palette && local.zoneMap) return { ...compact, ...local, zones: compact.zones || local.zones };
    if (compact.palette && compact.zoneMap) return compact;
    const productConfig = await getProductConfig(item);
    if (!productConfig) return compact;
    return { ...productConfig, ...compact, svgUrl: compact.svgUrl || productConfig.svgUrl };
  };

  const resolveCode = (zone, zones, visited = new Set()) => {
    const value = String(zones[zone] || '');
    if (!value.startsWith('__MATCH_')) return value;
    if (visited.has(zone)) return '';
    visited.add(zone);
    return resolveCode(value.replace('__MATCH_', ''), zones, visited);
  };

  const configuredSvg = async (data) => {
    if (!data.svgUrl || !data.zoneMap || !data.palette || !data.zones) return null;
    const source = await getSvgText(data.svgUrl);
    const parsed = new DOMParser().parseFromString(source, 'image/svg+xml');
    if (parsed.querySelector('parsererror')) return null;
    const svg = document.importNode(parsed.documentElement, true);
    Object.keys(data.zones).forEach((zone) => {
      const code = resolveCode(zone, data.zones);
      const meta = data.palette[code];
      const hex = typeof meta === 'string' ? meta : meta && meta.hex;
      if (!hex) return;
      (data.zoneMap[zone] || []).forEach((id) => {
        const element = svg.querySelector(`#${escapeId(id)}`);
        if (!element) return;
        element.style.fill = hex;
        element.style.stroke = hex;
      });
    });
    svg.removeAttribute('width');
    svg.removeAttribute('height');
    svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
    svg.setAttribute('role', 'img');
    svg.setAttribute('aria-label', `${data.brand || ''} ${data.model || ''} – konfigurierte Farbkombination`.trim());
    return svg;
  };

  const cleanVisibleConfigurationIds = () => {
    document.querySelectorAll('.f-cart-drawer__option').forEach((row) => {
      const label = row.querySelector('span:first-child')?.textContent?.trim() || '';
      if (/^Konfigurations-ID:/i.test(label)) row.remove();
    });
    document.querySelectorAll('.f-cart-item__details .product-option').forEach((row) => {
      const label = row.querySelector('dt')?.textContent?.trim() || '';
      if (/^Konfigurations-ID:?$/i.test(label)) row.remove();
    });
  };

  const renderItem = async (item) => {
    const data = await resolveData(item);
    if (!data || !data.svgUrl || !data.zones) return;
    const rows = Array.from(document.querySelectorAll('[data-cart-item]')).filter((row) => row.dataset.cartItem === item.key);
    if (!rows.length) return;
    const signature = `${item.key}|${data.reference || ''}|${JSON.stringify(data.zones || {})}`;
    const svg = await configuredSvg(data);
    if (!svg) return;

    rows.forEach((row) => {
      const target = row.querySelector('.f-cart-drawer__product-image') || row.querySelector('.f-cart-item__media');
      if (!target || target.dataset.paragliderPreviewSignature === signature) return;
      const link = target.querySelector('a')?.cloneNode(true);
      target.innerHTML = '';
      if (link) target.appendChild(link);
      const host = document.createElement('div');
      host.className = 'peakstyle-paraglider-cart-preview';
      host.appendChild(svg.cloneNode(true));
      target.appendChild(host);
      target.dataset.paragliderPreviewSignature = signature;
    });
  };

  const refresh = async () => {
    if (refreshRunning) { refreshQueued = true; return; }
    refreshRunning = true;
    try {
      injectStyles();
      cleanVisibleConfigurationIds();
      const response = await fetch(`${rootUrl()}cart.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const cart = await response.json();
      await Promise.all((cart.items || []).map(renderItem));
      cleanVisibleConfigurationIds();
    } catch (error) {
      console.warn('PeakStyle paraglider cart preview:', error);
    } finally {
      refreshRunning = false;
      if (refreshQueued) { refreshQueued = false; scheduleRefresh(80); }
    }
  };

  const scheduleRefresh = (delay = 120) => {
    window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(refresh, delay);
  };

  document.addEventListener('cart:updated', () => scheduleRefresh(40));
  document.addEventListener('cart:refresh', () => scheduleRefresh(350));
  document.addEventListener('DOMContentLoaded', () => scheduleRefresh(60), { once: true });

  const observer = new MutationObserver((mutations) => {
    const cartChanged = mutations.some((mutation) => {
      const target = mutation.target instanceof Element ? mutation.target : mutation.target.parentElement;
      if (target && target.closest('[data-cart-items], #cart-drawer, #main-cart-items')) return true;
      return Array.from(mutation.addedNodes).some((node) => node instanceof Element && (node.matches('[data-cart-item], [data-cart-items]') || node.querySelector('[data-cart-item]')));
    });
    if (cartChanged) scheduleRefresh(100);
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
  scheduleRefresh(100);
})();
